import React, { useCallback } from 'react'
import { Redirect, Route, Switch, useHistory } from 'react-router-dom'

import Loader from './components/Loader/Loader'
import { Auth0Provider } from '~/components/providers/Auth0Provider'
import ProtectedRoute from './components/providers/ProtectedRoute'
import { ExternalRedirect } from '~/components/redirect/Redirect'
import Success from './screens/success/Success'
import Visit from './screens/visit/Root'
import { APP_STORE_ANDROID, APP_STORE_IOS, mobileOS } from '~/utils'
import IneligibleState from './screens/success/IneligibleState'
import AccountNameForm from './screens/signup/steps/account/AccountNameForm'
import ProspectNameDobCollection from './screens/signup/steps/account/ProspectNameDobCollection'
import DemographicCollectionForm from './screens/signup/steps/account/DemographicCollectionForm'
import AccountInsuranceProvider from './screens/signup/steps/account/AccountInsuranceProvider'
import ConsentForm from './screens/signup/common/terms/ConsentForm'
import AccountCreation from './screens/success/AccountCreation'
import ExistingAccount from './screens/success/ExistingAccount'
import { ErrorScreen } from './components/error/ErrorScreen'
import { shouldShowErrorPage } from './components/error/utils'
import ChangeEmail from './screens/ChangeEmail'
import EmailVerification from './screens/EmailVerification'
import EmailVerified from './screens/EmailVerified'
import PhoneNumber from './screens/phonenumber/PhoneNumber'
import AccountIneligibleInsurance from './screens/signup/steps/account/AccountIneligibleInsurance'
import AccountTermsAndConditions from './screens/signup/steps/account/AccountTermsAndConditions'
import UserTypeSelection from './screens/signup/steps/account/UserTypeSelection'
import AuthenticatedSignupTypeSelection from './screens/signup/steps/account/authenticated/AuthenticatedSignupTypeSelection'
import AuthenticatedInviteCodeValidation from './screens/signup/steps/account/authenticated/AuthenticatedInviteCodeValidation'
import AuthenticatedInsurancePayerSelection from './screens/signup/steps/account/authenticated/AuthenticatedInsurancePayerSelection'
import AuthenticatedNameDobCollection from './screens/signup/steps/account/authenticated/AuthenticatedNameDobCollection'
import AuthenticatedProspectNameDobCollection from './screens/signup/steps/account/authenticated/AuthenticatedProspectNameDobCollection'
import AuthenticatedDemographicsCollection from './screens/signup/steps/account/authenticated/AuthenticatedDemographicsCollection'
import AuthenticatedMemberIdCollection from './screens/signup/steps/account/authenticated/AuthenticatedMemberIdCollection'
import InviteCode from './screens/signup/steps/account/InviteCode'
import Landing from './screens/signup/Landing'
import { AUTHENTICATED_SIGNUP_ROUTES } from '~/constants/ScreenKeyToRouteMapping'

const Root: React.FC = () => {
  const history = useHistory()
  const onRedirectCallback = useCallback(({ returnTo }) => {
    if (returnTo) {
      history.replace(returnTo)
    }
  }, [])

  const redirect =
    mobileOS === 'iOS'
      ? APP_STORE_IOS
      : mobileOS === 'Android'
        ? APP_STORE_ANDROID
        : window.location.origin + `/success`

  /**
   * Intercept with an error screen if we should show the error page. This is only
   * for catastrophic errors that we can't recover from.
   * Throwing an error in the render method will cause the error boundary to catch
   * and show the error screen.
   */
  if (shouldShowErrorPage) throw new Error('Forcing error screen to show. Cookies may be disabled.')

  const component = (
    <Auth0Provider
      onRedirectCallback={onRedirectCallback}
      screen_hint={
        window.location.pathname.match(/visit/) ||
        window.location.pathname.match(/emailverified/) ||
        window.location.pathname.match(/phonenumber/) ||
        window.location.pathname.match(/emailverification/)
          ? 'login'
          : 'signup'
      }
      redirectUri={window.location.origin + '/auth/callback'}
    >
      <Switch>
        <ProtectedRoute path="/visit" component={Visit} />
        <Route path="/success" exact component={Success} />
        <Route path="/signup/user-type" component={UserTypeSelection} />
        <ProtectedRoute
          path={AUTHENTICATED_SIGNUP_ROUTES.SIGNUP_TYPE_SELECTION}
          component={AuthenticatedSignupTypeSelection}
        />
        <ProtectedRoute
          path={AUTHENTICATED_SIGNUP_ROUTES.INVITE_CODE_VALIDATION}
          component={AuthenticatedInviteCodeValidation}
        />
        <ProtectedRoute
          path={AUTHENTICATED_SIGNUP_ROUTES.INSURANCE_PAYER_SELECTION}
          component={AuthenticatedInsurancePayerSelection}
        />
        <ProtectedRoute
          path={AUTHENTICATED_SIGNUP_ROUTES.NAME_DOB_COLLECTION}
          component={AuthenticatedNameDobCollection}
        />
        <ProtectedRoute
          path={AUTHENTICATED_SIGNUP_ROUTES.PROSPECT_NAME_DOB_COLLECTION}
          component={AuthenticatedProspectNameDobCollection}
        />
        <ProtectedRoute
          path={AUTHENTICATED_SIGNUP_ROUTES.DEMOGRAPHICS_COLLECTION}
          component={AuthenticatedDemographicsCollection}
        />
        <ProtectedRoute
          path={AUTHENTICATED_SIGNUP_ROUTES.MEMBER_ID_COLLECTION}
          component={AuthenticatedMemberIdCollection}
        />
        <Route path="/signup/invite-code" component={InviteCode} />
        <Route path="/signup/insurance/payer" component={AccountInsuranceProvider} />
        <Route path="/signup/insurance/ineligible" component={AccountIneligibleInsurance} />
        {/* signup/name/jpmc was exposed to jpmc users and if anyone tries to access this directly
        should now be redirected to insurance type screen. We have removed the
        insurance type and payer dropdownsfrom the Account form screen so
        insurance type should be selected before reaching the name screen */}
        <Redirect path="/signup/name/:employer" to="/signup/user-type" />
        <Route path="/signup/prospect-name" component={ProspectNameDobCollection} />
        <Route path="/signup/name" component={AccountNameForm} />
        <Route path="/termsandconditions" exact component={AccountTermsAndConditions} />
        <Route path="/emailverified" exact component={EmailVerified} />
        <ProtectedRoute path="/emailverification/change-email" exact component={ChangeEmail} />
        <ProtectedRoute path="/emailverification" exact component={EmailVerification} />
        <Route path="/signup/account" component={DemographicCollectionForm} />
        <Route path="/consent" exact component={ConsentForm} />
        <Route path="/signup/existing" component={ExistingAccount} />
        <Route path="/signup/cannot-service" component={IneligibleState} />
        <ProtectedRoute path="/signup/create-account" component={AccountCreation} />
        <Route path="/phonenumber" component={PhoneNumber} />
        <Route path="/auth/callback" component={Loader} />
        <ExternalRedirect path="/app" redirect={redirect} />
        <Route path="/policies" exact component={ConsentForm} />
        <Route path="/error" component={ErrorScreen} />
        {/* Redirect removed route to payer selection */}
        <Redirect from="/signup/insurance/type" to="/signup/insurance/payer" exact />
        <Route path="/" exact component={Landing} />
      </Switch>
    </Auth0Provider>
  )

  return component
}

export default Root
